import {Store} from '../models/store.model';
import {StoreDTO, StoreStatisticsDTO} from '../models/store.dto';
import {StoreStatistics} from '../models/store.model';
import {mapCreditInfoDTOToModel} from './creditInfo.mapper';
import {calculatePaymentStatus} from '../utils/paymentStatusUtils';

// Helper function to validate coordinates
const isValidCoordinate = (value: any): boolean => {
  return value !== undefined &&
         value !== null &&
         !isNaN(value) &&
         typeof value === 'number' &&
         value >= -90 &&
         value <= 90;
};

const isValidLongitude = (value: any): boolean => {
  return value !== undefined &&
         value !== null &&
         !isNaN(value) &&
         typeof value === 'number' &&
         value >= -180 &&
         value <= 180;
};


export const mapStoreDTOToModel = (dto: StoreDTO): Store => {
  // Validate and sanitize coordinates from latitude/longitude (backend sends latitude/longitude)
  const latitude = isValidCoordinate(dto.latitude) ? dto.latitude : 0;
  const longitude = isValidLongitude(dto.longitude) ? dto.longitude : 0;

  // Use city and region from DTO (will be provided by backend)
  const city = dto.city || 'غير محدد';
  const region = dto.region || 'غير محدد';


  const mappedStore = {
    id: dto.id,
    name: dto.name,
    address: dto.address || `${city}, ${region}`,
    paymentStatus: calculatePaymentStatus(dto.totalDue, dto.totalPaid),
    managerName: dto.managerName || dto.manager_name || 'غير محدد',
    profileImageUrl: dto.profileImageUrl,
    developerId: dto.developerId ?? dto.developer_id ?? null,
    developerName: dto.developerName || dto.developer_name || 'غير محدد',
    phoneNumber: dto.phoneNumber || 'غير محدد',
    latitude: latitude,
    longitude: longitude,
    region: region,
    city: city,
    postalCode: dto.postalCode || '00000',
    distance: dto.distance,
    quantityAvailable: dto.quantityAvailable,
    storedQuantityAvailable: dto.storedQuantityAvailable,
    lowStockThreshold: dto.lowStockThreshold,
    totalDue: dto.totalDue ? parseFloat(dto.totalDue) : undefined,
    totalPaid: dto.totalPaid ? parseFloat(dto.totalPaid) : undefined,
    images: dto.images || [],
    creditInfo: dto.creditInfo ? mapCreditInfoDTOToModel(dto.creditInfo) : undefined,
    // معلومات إضافية من API
    area: dto.area,
    status: dto.status,
    inventory: dto.inventory,
    notes: dto.notes,
    lastVisit: dto.lastVisit,
    lastPaymentDate: dto.lastPaymentDate,
    commercialRegisterNumber: dto.commercialRegisterNumber,
    commercialRegisterImage: dto.commercialRegisterImage,
    taxCardNumber: dto.taxCardNumber,
    taxCardImage: dto.taxCardImage,
    geo_location: dto.geo_location,
    created_at: dto.created_at,
    updated_at: dto.updated_at,
    // File upload fields
    commercialRegisterImageFile: dto.commercialRegisterImageFile,
    taxCardImageFile: dto.taxCardImageFile,
    profileImageFile: dto.profileImageFile,
    storeImagesFiles: dto.storeImagesFiles,
    // إضافة aliases للخريطة
    lat: latitude,
    lng: longitude,
    // أقسام التفاصيل من API (إن وُجدت)
    sections: dto.sections?.map(s => ({
      order: s.order,
      id: s.id,
      title: s.title,
      data: s.data || {}
    }))
  };

  return mappedStore;
};

export const mapStoreModelToDTO = (model: Store): StoreDTO => {
  const dto = {
    id: model.id,
    name: model.name,
    address: model.address,
    city: model.city,
    region: model.region,
    latitude: model.latitude,
    longitude: model.longitude,
    paymentStatus: model.paymentStatus,
    managerName: model.managerName,
    profileImageUrl: model.profileImageUrl,
    developerId: model.developerId,
    developerName: model.developerName,
    phoneNumber: model.phoneNumber,
    postalCode: model.postalCode,
    distance: model.distance,
    quantityAvailable: model.quantityAvailable,
    storedQuantityAvailable: model.storedQuantityAvailable,
    lowStockThreshold: model.lowStockThreshold,
    totalDue: model.totalDue ? model.totalDue.toString() : undefined,
    totalPaid: model.totalPaid ? model.totalPaid.toString() : undefined,
    images: model.images,
    // معلومات إضافية
    area: model.area,
    status: model.status,
    inventory: model.inventory,
    notes: model.notes,
    lastVisit: model.lastVisit,
    lastPaymentDate: model.lastPaymentDate,
    commercialRegisterNumber: model.commercialRegisterNumber,
    commercialRegisterImage: model.commercialRegisterImage,
    taxCardNumber: model.taxCardNumber,
    taxCardImage: model.taxCardImage,
    geo_location: model.geo_location,
    created_at: model.created_at,
    updated_at: model.updated_at,
    // File upload fields
    commercialRegisterImageFile: model.commercialRegisterImageFile,
    taxCardImageFile: model.taxCardImageFile,
    profileImageFile: model.profileImageFile,
    storeImagesFiles: model.storeImagesFiles
  };

  return dto;
};

export const mapStoresDTOToModel = (dtos: StoreDTO[]): Store[] => {
  return dtos.map(mapStoreDTOToModel);
};

// Mapper لإحصائيات المتاجر
export const mapStoreStatisticsDTOToModel = (dto: StoreStatisticsDTO): StoreStatistics => {
  return {
    totalStores: dto.totalStores,
    activeStores: dto.activeStores,
    inactiveStores: dto.inactiveStores,
    pendingPaymentStores: dto.pendingPaymentStores,
    lowStockStores: dto.lowStockStores,
    outOfStockStores: dto.outOfStockStores,
    activePercentage: dto.activePercentage,
    lastUpdated: dto.lastUpdated
  };
};
